/*
  11 #8 — a VO raised from site. The site engineer takes a photo, says where and what the client asked for (part
  and quantity); the server numbers it (VO-005 after VO-004), keeps it as Proposed with no value ("Needs
  pricing") and tells the project's Senior PE to review and price it. Without signal the sheet keeps what was
  typed and "Send" can be pressed again — the same request id means the VO is still made only once.
*/
import { useEffect, useMemo, useState } from 'react';
import { Camera, Loader2, Plus, Send, X } from 'lucide-react';
import api from '@/lib/api';
import { BottomSheet } from '@/components/ui/bottom-sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { notify } from '@/components/ui/notify';
import { getErrorMessage } from '@/lib/errors';
import { cn } from '@/lib/utils';
import { MONO } from './ProjectUi';
import { isOfflineError, newOpId } from './siteQueue';

const emptyLine = () => ({ key: newOpId(), part_number: '', description: '', qty: '' });
const MAX_PHOTOS = 4;

export function SiteVariationSheet({ open, onOpenChange, projectId, location = '', reviewer = '', onCreated }) {
  const [where, setWhere] = useState(location);
  const [description, setDescription] = useState('');
  const [lines, setLines] = useState([emptyLine()]);
  const [photos, setPhotos] = useState([]);
  const [opId, setOpId] = useState(newOpId);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setWhere(location);
    setDescription('');
    setLines([emptyLine()]);
    setPhotos([]);
    setOpId(newOpId());
    setError('');
  }, [open, location]);

  const previews = useMemo(() => photos.map((file) => ({ file, url: typeof URL !== 'undefined' && URL.createObjectURL ? URL.createObjectURL(file) : '' })), [photos]);
  useEffect(() => () => previews.forEach((preview) => preview.url && URL.revokeObjectURL?.(preview.url)), [previews]);

  const setLine = (key, field, value) => setLines((prev) => prev.map((line) => (line.key === key ? { ...line, [field]: value } : line)));

  const send = async () => {
    const items = lines
      .filter((line) => line.part_number.trim() || line.description.trim() || String(line.qty).trim())
      .map((line) => ({ part_number: line.part_number.trim(), description: line.description.trim(), qty: Number(String(line.qty).replace(',', '.')) }));
    if (description.trim().length < 3) {
      setError('Say what the client asked for');
      return;
    }
    const bad = items.find((item) => !(item.part_number || item.description) || !item.qty);
    if (bad) {
      setError('Each item needs a part number or a description, and a quantity');
      return;
    }
    const form = new FormData();
    form.append('description', description.trim());
    form.append('location', where.trim());
    form.append('items', JSON.stringify(items));
    form.append('op_id', opId);
    photos.forEach((file) => form.append('photos', file, file.name || 'photo.jpg'));
    setSending(true);
    setError('');
    try {
      const res = await api.post(`/projects/${projectId}/variations/site-request`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      notify.success(`${res.data?.vo_number || 'The VO'} sent${reviewer ? ` to ${reviewer}` : ''} for pricing`);
      onCreated?.(res.data);
      onOpenChange(false);
    } catch (err) {
      setError(isOfflineError(err)
        ? 'No signal — keep this open and press Send again when you are back online (it is sent only once).'
        : getErrorMessage(err, 'Could not send the request'));
    } finally {
      setSending(false);
    }
  };

  return (
    <BottomSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Request a variation"
      description={reviewer ? `${reviewer} reviews and prices it` : 'The Senior PE reviews and prices it'}
      testId="site-vo-sheet"
      footer={(
        <div className="space-y-2">
          {error ? <p className="text-[12.5px] font-semibold text-status-red-ink" role="alert" data-testid="site-vo-error">{error}</p> : null}
          <Button type="button" size="lg" className="h-[52px] w-full text-[15px]" onClick={send} disabled={sending} data-testid="site-vo-send">
            {sending ? <Loader2 className="animate-spin" aria-hidden="true" /> : <Send aria-hidden="true" />}
            Send for pricing
          </Button>
        </div>
      )}
    >
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2">
          {previews.map((preview, index) => (
            <span key={preview.url || index} className="relative h-20 w-20 overflow-hidden rounded-lg border border-line bg-surface-2">
              {preview.url ? <img src={preview.url} alt={`Photo ${index + 1}`} className="h-full w-full object-cover" /> : null}
              <button
                type="button"
                className="absolute right-1 top-1 grid h-6 w-6 place-items-center rounded-full bg-black/60 text-white"
                aria-label={`Remove photo ${index + 1}`}
                onClick={() => setPhotos((prev) => prev.filter((_, at) => at !== index))}
              >
                <X className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </span>
          ))}
          {photos.length < MAX_PHOTOS ? (
            <label className="grid h-20 w-20 cursor-pointer place-items-center rounded-lg border border-dashed border-[color:var(--line-dashed)] text-center text-[11.5px] font-semibold text-ink-3 hover:bg-surface-hover">
              <span className="flex flex-col items-center gap-1">
                <Camera className="h-5 w-5" aria-hidden="true" />
                Photo
              </span>
              <input
                type="file"
                accept="image/*"
                capture="environment"
                className="sr-only"
                aria-label="Add a photo"
                data-testid="site-vo-photo"
                onChange={(event) => {
                  const files = [...(event.target.files || [])];
                  setPhotos((prev) => [...prev, ...files].slice(0, MAX_PHOTOS));
                  event.target.value = '';
                }}
              />
            </label>
          ) : null}
        </div>
        <label className="block space-y-1">
          <span className="text-[12.5px] font-semibold text-ink-2">Where</span>
          <Input value={where} onChange={(event) => setWhere(event.target.value)} placeholder="Villa 4 (F) · Majlis" aria-label="Where" className="h-11 text-[15px]" maxLength={200} />
        </label>
        <label className="block space-y-1">
          <span className="text-[12.5px] font-semibold text-ink-2">What the client asked for</span>
          <Textarea value={description} onChange={(event) => setDescription(event.target.value)} placeholder="2 more glass push buttons next to the majlis door" aria-label="What the client asked for" className="min-h-[72px] text-[15px]" maxLength={500} />
        </label>
        <div className="space-y-2">
          <span className="block text-[12.5px] font-semibold text-ink-2">Items</span>
          {lines.map((line, index) => (
            <div key={line.key} className="grid grid-cols-[minmax(0,1fr)_72px_36px] items-start gap-2" data-testid={`site-vo-line-${index}`}>
              <div className="space-y-1.5">
                <Input value={line.part_number} onChange={(event) => setLine(line.key, 'part_number', event.target.value)} placeholder="Part no. (BE-GT20W.02)" aria-label={`Part number of item ${index + 1}`} className={cn('h-11 text-[14px]', MONO)} />
                <Input value={line.description} onChange={(event) => setLine(line.key, 'description', event.target.value)} placeholder="Description" aria-label={`Description of item ${index + 1}`} className="h-11 text-[14px]" />
              </div>
              <Input value={line.qty} onChange={(event) => setLine(line.key, 'qty', event.target.value)} inputMode="decimal" placeholder="Qty" aria-label={`Quantity of item ${index + 1}`} className="h-11 text-center text-[15px]" />
              <Button type="button" variant="ghost" size="icon" className="h-11 w-9" onClick={() => setLines((prev) => (prev.length > 1 ? prev.filter((entry) => entry.key !== line.key) : [emptyLine()]))} aria-label={`Remove item ${index + 1}`}>
                <X aria-hidden="true" />
              </Button>
            </div>
          ))}
          <Button type="button" variant="outline" className="h-11 w-full" onClick={() => setLines((prev) => [...prev, emptyLine()])}>
            <Plus aria-hidden="true" />
            Add item
          </Button>
        </div>
      </div>
    </BottomSheet>
  );
}
